/**
 * AuditBreakdownPanel — Kombi-Card aus „UStVA-Konsistenz-Hinweis" und
 * „Quellen aufschlüsseln" (Reconciliation).
 *
 * Layout:
 *   ┌──────────────────────────────────────────────┐
 *   │ ✓ USt-Werte UStVA-konform · § XX  [▼]        │ ← klickbarer Header (klappt alles)
 *   │   <optionaler Hinweis-Text>                  │
 *   ├──────────────────────────────────────────────┤
 *   │ Umsatzerlöse-Tabelle                         │
 *   │ Vorsteuer-Tabelle                            │
 *   │ Saldo-Karten                                 │
 *   └──────────────────────────────────────────────┘
 *
 * Props:
 *   breakdown            — audit_breakdown aus UStVA-Engine (Required).
 *   currencyFormatter    — (number) => "€ 1.234,56".
 *   headerTitle?         — z.B. "USt-Werte UStVA-konform" (optional).
 *   headerNote?          — Beschreibender Hinweis-Text (string oder ReactNode).
 *   defaultOpen?         — Initial expanded? (default: false)
 */
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  FileSpreadsheet,
  Calculator,
  CheckCircle,
} from "lucide-react";

const Row = ({ label, count, gross, net, vat, currencyFormatter }) => (
  <tr className="border-b border-slate-100 last:border-0">
    <td className="py-2 pr-4 text-slate-700">
      {label}
      {typeof count === "number" && count > 0 && (
        <Badge variant="secondary" className="ml-2 text-xs">{count} Stück</Badge>
      )}
    </td>
    <td className="py-2 px-2 text-right tabular-nums text-slate-600">
      {typeof gross === "number" ? currencyFormatter(gross) : "—"}
    </td>
    <td className="py-2 px-2 text-right tabular-nums text-slate-600">
      {typeof net === "number" ? currencyFormatter(net) : "—"}
    </td>
    <td className="py-2 pl-2 text-right tabular-nums font-medium text-slate-900">
      {typeof vat === "number" ? currencyFormatter(vat) : "—"}
    </td>
  </tr>
);

/** Feb 2026 — Vorsteuer-Row mit expliziten, eindeutig benannten Slots
 *  (Brutto / Netto / Vorsteuer voll / abzgl. Privatanteil / Vorsteuer wirksam).
 *  Vermeidet Re-Use der Generic-Row, deren gross/net/vat Slots zu
 *  Verwechslungen führten.
 */
const VatRow = ({ label, count, gross, net, vatFull, privateReduction, vatEffective, currencyFormatter, emphasis = false }) => (
  <tr className="border-b border-slate-100 last:border-0">
    <td className="py-2 pr-4 text-slate-700">
      {label}
      {typeof count === "number" && count > 0 && (
        <Badge variant="secondary" className="ml-2 text-xs">{count} Stück</Badge>
      )}
    </td>
    <td className="py-2 px-2 text-right tabular-nums text-slate-600">
      {typeof gross === "number" ? currencyFormatter(gross) : "—"}
    </td>
    <td className="py-2 px-2 text-right tabular-nums text-slate-600">
      {typeof net === "number" ? currencyFormatter(net) : "—"}
    </td>
    <td className="py-2 px-2 text-right tabular-nums text-slate-600">
      {typeof vatFull === "number" ? currencyFormatter(vatFull) : "—"}
    </td>
    <td className="py-2 px-2 text-right tabular-nums text-rose-700">
      {typeof privateReduction === "number" && privateReduction !== 0 ? currencyFormatter(privateReduction) : "—"}
    </td>
    <td className={`py-2 pl-2 text-right tabular-nums font-medium ${emphasis ? "text-slate-900 font-bold" : "text-slate-900"}`}>
      {typeof vatEffective === "number" ? currencyFormatter(vatEffective) : "—"}
    </td>
  </tr>
);

export default function AuditBreakdownPanel({
  breakdown,
  currencyFormatter,
  headerTitle = "USt-Werte UStVA-konform",
  headerNote = null,
  defaultOpen = false,
  noteCollapsed = false,
  onToggleNote = null,
}) {
  const [localOpen, setLocalOpen] = useState(defaultOpen);
  const controlled = typeof onToggleNote === 'function';
  const open = controlled ? !noteCollapsed : localOpen;
  const toggle = () => (controlled ? onToggleNote() : setLocalOpen(!localOpen));
  if (!breakdown) return null;

  const fmt = currencyFormatter || ((n) => `${(n || 0).toFixed(2)} €`);
  const rev = breakdown.revenue || {};
  const iv = breakdown.input_vat || {};
  const bal = breakdown.balance || {};
  const vstp = breakdown.vorsteuer_pauschalierung;  // Juni 2026 — null wenn inaktiv

  return (
    <Card className="border-emerald-200 bg-emerald-50/40" data-testid="audit-breakdown-panel">
      {/* ── Klickbarer Header (klappt alles auf/zu) ── */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full text-left px-4 py-3 hover:bg-emerald-50/80 transition-colors rounded-t-lg focus:outline-none focus:ring-2 focus:ring-emerald-300"
        data-testid="audit-breakdown-toggle"
      >
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-emerald-900">{headerTitle}</h4>
              <span className="text-emerald-700 shrink-0" aria-hidden="true" data-testid="hint-ustva-toggle">
                {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </span>
            </div>
          </div>
        </div>
      </button>
      {headerNote && open && (
        <div className="px-4 pb-3 -mt-1 pl-12 text-sm text-emerald-900/90 leading-snug" data-testid="audit-breakdown-note">{headerNote}</div>
      )}

      {/* ── Reconciliation-Detail (auf-/einklappbar) ── */}
      {open && (
        <CardContent className="px-4 pb-4 pt-0 border-t border-emerald-200/60" data-testid="audit-breakdown-content">
          <div className="space-y-6 text-sm mt-4">
            {/* === Umsatzerlöse === */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-emerald-600" />
                <h5 className="font-semibold text-emerald-900">Umsatzerlöse</h5>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500 border-b border-slate-200">
                    <th className="text-left py-1.5 pr-4 font-medium">Quelle</th>
                    <th className="text-right py-1.5 px-2 font-medium">Brutto</th>
                    <th className="text-right py-1.5 px-2 font-medium">Netto</th>
                    <th className="text-right py-1.5 pl-2 font-medium">USt eingenommen</th>
                  </tr>
                </thead>
                <tbody>
                  <Row label={rev.invoices?.label || "Rechnungen (Einnahmen)"}
                    count={rev.invoices?.count} gross={rev.invoices?.gross}
                    net={rev.invoices?.net} vat={rev.invoices?.vat}
                    currencyFormatter={fmt} />
                  <Row label={rev.manual_incomes?.label || "Manuelle Einnahmen"}
                    count={rev.manual_incomes?.count} gross={rev.manual_incomes?.gross}
                    net={rev.manual_incomes?.net} vat={rev.manual_incomes?.vat}
                    currencyFormatter={fmt} />
                  <Row label={rev.rc_outbound?.label || "Reverse-Charge Ausgangsleistungen"}
                    count={rev.rc_outbound?.count} net={rev.rc_outbound?.net} vat={0}
                    currencyFormatter={fmt} />
                  <Row label={rev.intra_eu_supplies?.label || "Innergemeinschaftliche Lieferungen"}
                    count={rev.intra_eu_supplies?.count} net={rev.intra_eu_supplies?.net} vat={0}
                    currencyFormatter={fmt} />
                </tbody>
              </table>
            </section>

            {/* === Vorsteuer === */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                <h5 className="font-semibold text-blue-900">Vorsteuer</h5>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500 border-b border-slate-200">
                    <th className="text-left py-1.5 pr-4 font-medium">Quelle</th>
                    <th className="text-right py-1.5 px-2 font-medium">Brutto</th>
                    <th className="text-right py-1.5 px-2 font-medium">Netto</th>
                    <th className="text-right py-1.5 px-2 font-medium">VSt voll</th>
                    <th className="text-right py-1.5 px-2 font-medium">abzgl. PrivAnt</th>
                    <th className="text-right py-1.5 pl-2 font-medium">VSt wirksam</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Reguläre Belege: voll = wirksam, kein Privatanteil */}
                  <VatRow
                    label={(iv.regular_expenses?.label || "Reguläre Vorsteuer-Belege") + (vstp?.active ? " (durch Pauschale ersetzt)" : "")}
                    count={iv.regular_expenses?.count}
                    gross={
                      typeof iv.regular_expenses?.net === "number" && typeof iv.regular_expenses?.vat === "number"
                        ? iv.regular_expenses.net + iv.regular_expenses.vat
                        : undefined
                    }
                    net={iv.regular_expenses?.net}
                    vatFull={iv.regular_expenses?.vat}
                    vatEffective={vstp?.active ? 0 : iv.regular_expenses?.vat}
                    currencyFormatter={fmt}
                  />
                  {/* Aktivierte Anlagen: voll vs. wirksam mit Privatanteil-Spalte */}
                  <VatRow
                    label={iv.capitalized_assets?.label || "Aktivierte Anlagen"}
                    count={iv.capitalized_assets?.count}
                    gross={
                      typeof iv.capitalized_assets?.net === "number" && typeof iv.capitalized_assets?.vat_full === "number"
                        ? iv.capitalized_assets.net + iv.capitalized_assets.vat_full
                        : undefined
                    }
                    net={iv.capitalized_assets?.net}
                    vatFull={iv.capitalized_assets?.vat_full}
                    privateReduction={
                      typeof iv.capitalized_assets?.vat_full === "number" && typeof iv.capitalized_assets?.vat_after_private === "number"
                        ? -(iv.capitalized_assets.vat_full - iv.capitalized_assets.vat_after_private)
                        : undefined
                    }
                    vatEffective={iv.capitalized_assets?.vat_after_private}
                    currencyFormatter={fmt}
                  />
                  {/* Innergem. Erwerb: voll = wirksam */}
                  <VatRow
                    label={iv.intra_eu_acquisitions?.label || "Innergem. Erwerb-Vorsteuer"}
                    count={iv.intra_eu_acquisitions?.count}
                    gross={
                      typeof iv.intra_eu_acquisitions?.net === "number" && typeof iv.intra_eu_acquisitions?.vat === "number"
                        ? iv.intra_eu_acquisitions.net + iv.intra_eu_acquisitions.vat
                        : undefined
                    }
                    net={iv.intra_eu_acquisitions?.net}
                    vatFull={iv.intra_eu_acquisitions?.vat}
                    vatEffective={iv.intra_eu_acquisitions?.vat}
                    currencyFormatter={fmt}
                  />
                </tbody>
              </table>

              {/* Juni 2026 — Vorsteuerpauschalierung (AT): Aufstellung der wirksamen Vorsteuer */}
              {vstp?.active && (
                <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-3 text-xs" data-testid="audit-vstp-block">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span aria-hidden>🧮</span>
                    <span className="font-semibold text-emerald-900">
                      Vorsteuerpauschalierung aktiv — {vstp.label} ({Number(vstp.rate).toLocaleString("de-DE", { minimumFractionDigits: 1 })} %)
                    </span>
                  </div>
                  <p className="text-emerald-800/80 leading-snug mb-2">
                    Die tatsächliche Beleg-Vorsteuer der regulären Belege ({fmt(vstp.actual_input_vat)}) wird durch die
                    Pauschale ersetzt. Anlagen-Vorsteuer, Wareneinkauf/Fremdleistung, innergem. Erwerb und § 15a bleiben
                    zusätzlich abziehbar.
                  </p>
                  <table className="w-full">
                    <tbody className="text-slate-700">
                      <tr className="border-t border-emerald-200">
                        <td className="py-1 pr-4">Pauschale ({Number(vstp.rate).toLocaleString("de-DE", { minimumFractionDigits: 1 })} % auf {fmt(vstp.base_net)})</td>
                        <td className="py-1 text-right tabular-nums font-medium">{fmt(vstp.pauschale)}</td>
                      </tr>
                      {typeof vstp.asset_sonder === "number" && vstp.asset_sonder !== 0 && (
                        <tr className="border-t border-emerald-100">
                          <td className="py-1 pr-4">+ Anlagen (Sonder-Vorsteuer)</td>
                          <td className="py-1 text-right tabular-nums">{fmt(vstp.asset_sonder)}</td>
                        </tr>
                      )}
                      {typeof vstp.goods_labor_sonder === "number" && vstp.goods_labor_sonder !== 0 && (
                        <tr className="border-t border-emerald-100">
                          <td className="py-1 pr-4">+ Wareneinkauf / Fremdleistung (Sonder-Vorsteuer)</td>
                          <td className="py-1 text-right tabular-nums">{fmt(vstp.goods_labor_sonder)}</td>
                        </tr>
                      )}
                      {typeof vstp.intra_acq_vat === "number" && vstp.intra_acq_vat !== 0 && (
                        <tr className="border-t border-emerald-100">
                          <td className="py-1 pr-4">+ Innergem. Erwerb</td>
                          <td className="py-1 text-right tabular-nums">{fmt(vstp.intra_acq_vat)}</td>
                        </tr>
                      )}
                      {typeof vstp.paragraph_15a === "number" && vstp.paragraph_15a !== 0 && (
                        <tr className="border-t border-emerald-100">
                          <td className="py-1 pr-4">+ § 15a-Korrektur</td>
                          <td className="py-1 text-right tabular-nums">{fmt(vstp.paragraph_15a)}</td>
                        </tr>
                      )}
                      <tr className="border-t-2 border-emerald-300">
                        <td className="py-1 pr-4 font-semibold text-emerald-900">= Σ Vorsteuer wirksam</td>
                        <td className="py-1 text-right tabular-nums font-bold text-emerald-900">{fmt(bal.input_vat_total)}</td>
                      </tr>
                    </tbody>
                  </table>
                  {vstp.capped && (
                    <p className="mt-2 text-amber-800">
                      ⚠️ Pauschale auf Höchstbetrag {fmt(vstp.cap)} gedeckelt.
                    </p>
                  )}
                  {vstp.turnover_limit && (
                    <p className="mt-1 text-emerald-800/70">
                      Voraussetzung: Vorjahresumsatz ≤ {Number(vstp.turnover_limit).toLocaleString("de-DE")} €.
                    </p>
                  )}
                </div>
              )}

              {/* Vorsteuer-Korrekturen (§ 15a UStG / § 12 Abs. 10–13 UStG) */}
              {typeof iv.paragraph_15a?.correction === "number" && iv.paragraph_15a.correction !== 0 && (
                <div className="mt-3 rounded border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-amber-900">
                      <span className="font-semibold">Vorsteuer-Korrektur:</span> {iv.paragraph_15a.label || "Vorsteuerberichtigung"}
                      {typeof iv.paragraph_15a?.count === "number" && iv.paragraph_15a.count > 0 && (
                        <Badge variant="secondary" className="ml-2">{iv.paragraph_15a.count} Stück</Badge>
                      )}
                    </span>
                    <span className={`tabular-nums font-semibold ${iv.paragraph_15a.correction >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {iv.paragraph_15a.correction >= 0 ? "+" : ""}{fmt(iv.paragraph_15a.correction)}
                    </span>
                  </div>
                  <p className="mt-1 text-amber-800/80 leading-snug">
                    Periodische Berichtigung der Vorsteuer aus Anlagevermögen. Wirkt auf „Σ Vorsteuer wirksam".
                  </p>
                </div>
              )}
            </section>

            {/* === Saldo === */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="h-4 w-4 text-amber-600" />
                <h5 className="font-semibold text-amber-900">USt-Saldo</h5>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded border border-slate-200 p-3 bg-white">
                  <div className="text-xs text-slate-500">Σ USt eingenommen</div>
                  <div className="font-semibold tabular-nums text-slate-900">{fmt(bal.revenue_vat_total)}</div>
                </div>
                <div className="rounded border border-slate-200 p-3 bg-white">
                  <div className="text-xs text-slate-500">Σ Vorsteuer wirksam</div>
                  <div className="font-semibold tabular-nums text-slate-900">{fmt(bal.input_vat_total)}</div>
                </div>
                <div className={`rounded border p-3 ${bal.payable_vat >= 0 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-100"}`}>
                  <div className="text-xs text-slate-600">{bal.payable_vat >= 0 ? "Zahllast" : "Erstattung"}</div>
                  <div className={`font-bold tabular-nums ${bal.payable_vat >= 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {fmt(Math.abs(bal.payable_vat))}
                  </div>
                </div>
              </div>
            </section>

            <p className="text-xs text-slate-500 italic pt-2 border-t border-slate-200">
              Diese Aufschlüsselung stammt direkt aus der UStVA-Engine und ist mit den Kennzahlen
              deiner Umsatzsteuer-Voranmeldung identisch. Alle drei Views (UStVA, Steuern-Tab, Finanzen)
              greifen auf dieselbe Quelle zu.
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
